import type { Company } from "@/components/atomic-crm/types";
import { parseLbsClientContextLinks } from "@/modules/clients/clientContextLinks";

type EmailRow = { email?: unknown; type?: unknown };
type PhoneRow = { number?: unknown; type?: unknown };

const PREFIX = "lbs:";

/** Replace only the given `lbs:` keys; every other link is kept as is. */
export const replaceLbsLinks = (
  links: string[] | null | undefined,
  entries: Record<string, string | null>,
) => {
  const keys = Object.keys(entries);
  const kept = (links ?? []).filter(
    (link) => !keys.some((key) => link.startsWith(`${PREFIX}${key}=`)),
  );
  for (const [key, value] of Object.entries(entries)) {
    if (value) kept.push(`${PREFIX}${key}=${value}`);
  }
  return kept;
};

const cleanType = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : "Work";

/** Company's own email rows (array, or the legacy single business email). */
export const getCompanyEmailRows = (
  company: Pick<Company, "context_links">,
) => {
  const ctx = parseLbsClientContextLinks(company.context_links);
  if (ctx.companyEmails?.length) return ctx.companyEmails;
  const business = ctx.businessEmail?.trim();
  return business ? [{ email: business, type: "Work" as const }] : [];
};

/** Company's own phone rows (array, or the `phone_number` column). */
export const getCompanyPhoneRows = (
  company: Pick<Company, "context_links" | "phone_number">,
) => {
  const ctx = parseLbsClientContextLinks(company.context_links);
  if (ctx.companyPhones?.length) return ctx.companyPhones;
  const phone = company.phone_number?.trim();
  return phone ? [{ number: phone, type: "Work" as const }] : [];
};

/**
 * Patch for the company's emails. A single plain address keeps the legacy
 * `business_email` shape; several addresses or a typed one (e.g. Billing)
 * use `company_emails`, which keeps each row's type.
 */
export const buildCompanyEmailsPatch = (
  company: Pick<Company, "context_links">,
  rows: EmailRow[],
) => {
  const emails = rows
    .map((row) => ({
      email: String(row.email ?? "").trim(),
      type: cleanType(row.type),
    }))
    .filter((row) => row.email);
  const existing = parseLbsClientContextLinks(company.context_links);
  const hadArray = (existing.companyEmails?.length ?? 0) > 0;
  const hadBusiness = Boolean(existing.businessEmail?.trim());
  const useArray =
    emails.length > 1 || hadArray || emails.some((row) => row.type !== "Work");
  const primary = emails[0]?.email ?? null;
  return {
    context_links: replaceLbsLinks(company.context_links, {
      company_emails: useArray && emails.length ? JSON.stringify(emails) : null,
      business_email: !useArray || hadBusiness ? primary : null,
    }),
  };
};

/** Patch for the company's phones: `phone_number` is the first row. */
export const buildCompanyPhonesPatch = (
  company: Pick<Company, "context_links">,
  rows: PhoneRow[],
) => {
  const phones = rows
    .map((row) => ({
      number: String(row.number ?? "").trim(),
      type: cleanType(row.type),
    }))
    .filter((row) => row.number);
  return {
    phone_number: phones[0]?.number ?? null,
    context_links: replaceLbsLinks(company.context_links, {
      company_phones: phones.length ? JSON.stringify(phones) : null,
    }),
  };
};
